// v0.7 daily habits on the client: the login-streak envelope and the missions card.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';

const hoursLeft = (ms: number) => ms > 3_600_000 ? `${Math.floor(ms / 3_600_000)} hr` : `${Math.max(1, Math.ceil(ms / 60_000))} min`;
import { Panel, Loading, ItemName } from './ui.tsx';
import { play } from '../fx/sound.ts';
import { motionFull } from '../fx/settings.ts';
import { fx, centerOf } from '../fx/fx.tsx';

const SEEN_KEY = 'mm-streak-popup';

/** One envelope per day of the week-long ladder. */
function Envelope({ day, label, state, opening }: { day: number; label: string; state: 'past' | 'today' | 'future'; opening?: boolean }) {
  return (
    <div className={`envelope ${state} ${opening ? 'opening' : ''} ${day === 7 ? 'big' : ''}`}>
      <svg viewBox="0 0 64 44" width="64" height="44" aria-hidden="true">
        <rect x="1" y="6" width="62" height="37" fill={state === 'past' ? '#d9cdb2' : '#f6eedc'} stroke="#1d1a16" strokeWidth="1.2" />
        <path className="flap" d="M1 6 L32 28 L63 6" fill={state === 'past' ? '#cfc2a4' : '#efe2c4'} stroke="#1d1a16" strokeWidth="1.2" />
        {state !== 'past' && <circle cx="32" cy="27" r="5" fill="#b3261e" stroke="#7e1a14" />}
        {state === 'past' && <path d="M22 24 l7 7 l14 -14" fill="none" stroke="#1f5d2c" strokeWidth="3" />}
      </svg>
      <div className="env-day">Day {day}</div>
      <div className="env-label">{label}</div>
    </div>
  );
}

function Ladder({ streak, opening }: { streak: any; opening?: boolean }) {
  return (
    <div className="envelopes">
      {streak.prizes.map((p: any) => (
        <Envelope key={p.day} day={p.day} label={p.label}
          state={p.day < streak.day || (!streak.claimable && p.day === streak.day) ? 'past' : p.day === streak.day ? 'today' : 'future'}
          opening={opening && p.day === streak.day} />
      ))}
    </div>
  );
}

/** Shown once a day when there's a streak reward to pick up. */
export function StreakPopup() {
  const { me, act } = useGame();
  const [streak, setStreak] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [done, setDone] = useState<any>(null);
  useEffect(() => {
    if (!me?.streak?.claimable) return;
    let seen = '';
    try { seen = localStorage.getItem(SEEN_KEY) ?? ''; } catch { /* ignore */ }
    const today = new Date().toDateString();
    if (seen === today) return;
    api.get('/api/daily').then((d) => {
      setStreak(d.streak);
      setOpen(true);
      play('envelope');
    }).catch(() => {});
  }, [me?.streak?.claimable]);
  if (!open || !streak) return null;
  const close = () => {
    try { localStorage.setItem(SEEN_KEY, new Date().toDateString()); } catch { /* ignore */ }
    setOpen(false);
  };
  const claim = async () => {
    setOpening(true);
    const r = await act('/api/streak/claim');
    if (!r) { setOpening(false); return; }
    play('chest');
    const at = centerOf(document.querySelector('.envelope.today')) ?? null;
    fx.coins(22, at);
    if (r.prize.cash) fx.float(`+${money(r.prize.cash)}`, 'cash', at, 300);
    setDone(r);
    setStreak({ ...streak, claimable: false, day: r.day });
    try { localStorage.setItem(SEEN_KEY, new Date().toDateString()); } catch { /* ignore */ }
  };
  return (
    <div className="modal-back" onClick={close}>
      <div className="modal streak-modal" role="dialog" aria-label="Daily reward" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">The Daily Envelope</div>
        <div className="modal-body">
          {streak.broken && !done && <div className="flash bad" style={{ marginBottom: 8 }}><div>You missed a day, so your streak starts over at day 1. Show up every day to climb back to the big one.</div></div>}
          <p style={{ marginTop: 0 }}>
            {done ? <>Day <b>{done.day}</b> collected: <b>{done.prize.label}</b>.{done.prize.itemId && <> The <ItemName itemId={done.prize.itemId} /> is in your inventory.</>}</>
              : <>Come by every day for a bigger envelope. Today is <b>day {streak.day}</b> of 7. Miss a day and it’s back to day 1.</>}
          </p>
          <Ladder streak={streak} opening={opening} />
          <div className="right" style={{ marginTop: 12 }}>
            {done ? <button className="btn big" onClick={close}>Back to work</button>
              : <><a className="small" onClick={close} style={{ marginRight: 12 }}>Later</a><button className="btn big green" disabled={opening} onClick={claim}>Open today’s envelope</button></>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The streak and today's missions, as a card for the Home page. */
export function DailyPanel() {
  const { me, act } = useGame();
  const { data, reload } = useDaily([me?.missions?.done, me?.streak?.claimable, me?.missions?.doneTotal]);
  if (!data) return <Panel title="Today’s Missions"><Loading /></Panel>;
  const m = data.missions;
  const done = m.daily.filter((x: any) => x.done).length;
  const reroll = async (slot: number) => {
    if (await act('/api/missions/reroll', { slot })) { play('typewriter'); reload(); }
  };
  return (
    <Panel title={<>Today’s Missions <span className="small">{done}/{m.daily.length}</span></>} right={<span className="small dim">new ones in {hoursLeft(m.resetsAt - Date.now())}</span>}>
      <div className="missions">
        {m.daily.map((x: any) => (
          <div key={x.slot} className={`mission ${x.done ? 'done' : ''}`}>
            <div className="mission-box" aria-hidden="true">{x.done ? '✓' : ''}</div>
            <div className="grow">
              <div className="mission-title">{x.title}</div>
              {!x.done && (
                <div className="mission-progress">
                  <div className="progress"><div style={{ width: `${(x.progress / x.target) * 100}%` }} /></div>
                  <span className="small">{x.money ? money(x.progress) : fmt(x.progress)} / {x.money ? money(x.target) : fmt(x.target)}</span>
                </div>
              )}
              <div className="small">{x.done ? <span className="good">Paid</span> : <>Pays <span className="money">{money(x.cash)}</span> + <span className="xp">{x.xp} XP</span></>}
                {!x.done && m.rerollsLeft > 0 && <> · <a onClick={() => reroll(x.slot)} title="Swap this mission for another one (once a day)">swap</a></>}
              </div>
            </div>
            {x.done && <span className="mission-stamp">Done</span>}
          </div>
        ))}
      </div>
      <div className={`mission-bonus ${m.bonusDone ? 'done' : ''}`}>
        {m.bonusDone ? <>Bonus chest collected. See you tomorrow.</> : <>Finish all three for a bonus chest: <span className="money">{money(m.bonus.cash)}</span>, {m.bonus.xp} XP and a favor point.</>}
      </div>
      {m.weekly && (
        <div className={`mission weekly ${m.weekly.done ? 'done' : ''}`}>
          <div className="mission-box" aria-hidden="true">{m.weekly.done ? '✓' : 'W'}</div>
          <div className="grow">
            <div className="mission-title">This week: {m.weekly.title}</div>
            {!m.weekly.done && (
              <div className="mission-progress">
                <div className="progress"><div style={{ width: `${(m.weekly.progress / m.weekly.target) * 100}%` }} /></div>
                <span className="small">{m.weekly.money ? money(m.weekly.progress) : fmt(m.weekly.progress)} / {m.weekly.money ? money(m.weekly.target) : fmt(m.weekly.target)}</span>
              </div>
            )}
            <div className="small">Pays <span className="money">{money(m.weekly.cash)}</span> + {m.weekly.xp} XP + {m.weekly.favor} favor points</div>
          </div>
        </div>
      )}
      <div className="streak-line">
        <span>Login streak: <b>{data.streak.streak}</b> day{data.streak.streak === 1 ? '' : 's'} (best {data.streak.best})</span>
        <span className="streak-dots" aria-label={`Day ${data.streak.day} of 7`}>
          {[1, 2, 3, 4, 5, 6, 7].map((d) => <i key={d} className={d < data.streak.day || (!data.streak.claimable && d === data.streak.day) ? 'on' : d === data.streak.day ? 'today' : ''} />)}
        </span>
      </div>
    </Panel>
  );
}

function useDaily(deps: unknown[]) {
  const [data, setData] = useState<any>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    api.get('/api/daily').then(setData).catch(() => {});
  }, [...deps, n]);
  return { data, reload: () => setN((x) => x + 1) };
}

/** "While you were gone…" typed out like a telegram, with a quiet typewriter sound. */
export function AwayStory({ text }: { text: string }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    if (!motionFull()) { setShown(text.length); return; }
    play('typewriter');
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(text.length, i + 3);
      setShown(i);
      if (i >= text.length) clearInterval(t);
    }, 18);
    return () => clearInterval(t);
  }, [text]);
  return (
    <div className="away-story" onClick={() => setShown(text.length)}>
      {text.slice(0, shown)}{shown < text.length && <span className="caret">▌</span>}
    </div>
  );
}
